import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import css from './views.module.css'
import { Card, ViewHeader, StageTrack, StatCard, MicroLabel, Empty, QueryError, Button, Notice, Disclosure } from '../ui'
import { useDepartments, useLaneTypes, useRuns, POLL, RUNS_WINDOW } from '../data/queries'
import { toLanes, medianCost, type Lane } from '../data/map/lanes'
import { ticketRuns, toTickets } from '../data/map/tickets'
import { summarise } from '../data/map/costs'
import { departmentLabel, departmentsFrom } from '../data/map/department'
import { laneGroupLine, lanesByDepartment, ticketsLine } from '../data/map/summaries'
import type { DepartmentRow } from '../lib/database.types'
import { usd } from '../lib/money'
import { age } from '../lib/time'

/**
 * The department filter is one lens, not three: the same `?dept=` the queue and
 * the activity feed read, so it survives moving between them. Everything below
 * the pills -- the lane list and the three stat cards -- reads the narrowed
 * runs, because a board that says "4 runs today" under a department filter
 * showing one lane is worse than no number at all.
 */
export function Lanes() {
  const [params, setParams] = useSearchParams()
  const dept = params.get('dept') ?? 'All'
  // Two reads, deduplicated by key when no filter is set. The unfiltered one
  // feeds the pills (below); the narrowed one is fetched per department, so a
  // filter shows that department's own window rather than its share of the
  // fleet's.
  const runs = useRuns()
  const narrowed = useRuns(dept === 'All' ? null : dept)
  const depts = useDepartments()

  // The pills come from the table plus whatever slugs the runs carry, and from
  // the *unfiltered* runs: a list that shrank to the one department you already
  // picked is a filter you cannot get out of.
  const departments = useMemo(
    () => departmentsFrom(depts.data ?? [], runs.data ?? []),
    [depts.data, runs.data],
  )
  const selected = useMemo(() => narrowed.data ?? [], [narrowed.data])
  const lanes = useMemo(() => toLanes(selected), [selected])
  const laneGroups = useMemo(() => lanesByDepartment(lanes), [lanes])
  // The same filtered rows the lanes above are built from, regrouped: a lane is
  // a slot and a run is one trip through it, and neither says how a ticket is
  // going. No extra query -- this is arithmetic on rows already in hand.
  // Less the report lanes' runs, which are not tickets (map/tickets.ts).
  const laneTypes = useLaneTypes()
  const tickets = useMemo(() => toTickets(ticketRuns(selected, laneTypes.data)), [selected, laneTypes.data])
  const anyLanes = useMemo(() => toLanes(runs.data ?? []).length > 0, [runs.data])
  const summary = useMemo(() => summarise(selected), [selected])

  const setParam = useCallback(
    (key: string, value: string | null) => {
      const next = new URLSearchParams(params)
      if (!value || value === 'All') next.delete(key)
      else next.set(key, value)
      setParams(next, { replace: true })
    },
    [params, setParams],
  )

  if (runs.error) return <QueryError error={runs.error} />
  if (narrowed.error) return <QueryError error={narrowed.error} />
  if (!runs.data || !narrowed.data) return <Card><div style={{ padding: 22, color: 'var(--ink-3)' }}>Reading lanes…</div></Card>

  // Day one is derived from the data, not toggled. Everything that implies
  // history has to be suppressed together, and a single source is what makes
  // that hold -- including the filter, which has nothing to filter.
  if (!anyLanes) {
    return (
      <>
        <ViewHeader title="Lanes" />
        <Card>
          <Empty glyph="[ ]">
            Nothing is running yet. Lanes appear here the first time the dispatcher queues a run —
            nothing needs to be configured in this dashboard for that to happen.
          </Empty>
        </Card>
      </>
    )
  }

  return (
    <>
      <ViewHeader
        title="Lanes"
        aside={`Auto-refresh every ${POLL.lanes / 1000}s${runs.isFetching || narrowed.isFetching ? ' · reading' : ''}`}
      />

      <div className={css.filters} style={{ marginBottom: 14 }}>
        <button
          className={dept === 'All' ? `${css.filterPill} ${css.filterActive}` : css.filterPill}
          onClick={() => setParam('dept', 'All')}
        >
          All departments
        </button>
        {departments.map((d) => (
          <button
            key={d.slug}
            className={dept === d.slug ? `${css.filterPill} ${css.filterActive}` : css.filterPill}
            onClick={() => setParam('dept', d.slug)}
          >
            {d.label}
          </button>
        ))}
      </div>

      {lanes.length === 0 ? (
        <Card>
          {/* The fleet is demonstrably busy -- it just is not busy here. Saying
              "nothing is running yet" would be a lie told by a filter. */}
          <Empty glyph="[-_-]">
            No lanes in {departmentLabel(dept, depts.data ?? [])}. The fleet is running; this
            department has not put anything in a lane. Other departments are unaffected.
          </Empty>
        </Card>
      ) : (
        // One section per department, closed to a line of what its lanes are
        // doing whenever there is more than one to choose between. A single
        // department -- or a filter down to one -- opens, because a board with
        // one closed row on it is a click that tells nobody anything.
        <div className={css.laneGroups}>
          {laneGroups.map((group) => (
            <Disclosure
              key={`${dept}:${group.department}`}
              flush
              title={departmentLabel(group.department, depts.data ?? [])}
              summary={laneGroupLine(group.lanes)}
              defaultOpen={laneGroups.length === 1}
            >
              <div className={css.laneList}>
                {group.lanes.map((lane) => (
                  <LaneCard key={lane.key} lane={lane} departments={depts.data ?? []} />
                ))}
              </div>
            </Disclosure>
          ))}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10, marginTop: 14 }}>
        <StatCard
          label="Runs today"
          value={String(summary.todayRuns)}
          note={summary.todayRuns === 0 ? 'nothing dispatched today' : 'dispatched since midnight'}
        />
        <StatCard
          label="Median run cost"
          value={medianCost(selected)}
          note={`across ${selected.length} ${selected.length === 1 ? 'run' : 'runs'} on record`}
        />
        <StatCard
          label="Oldest item awaiting a person"
          value="—"
          note="nothing writes approvals yet"
        />
      </div>

      {/* One line per ticket. A ticket that is having a hard time reads as the
          shape of its runs rather than as a score, and it is not lifted above a
          clean one: this is a record of how work is going, not a problem list. */}
      {tickets.length > 0 ? (
        <Card style={{ marginTop: 14 }}>
          <Disclosure
            label
            title="By ticket"
            summary={`${ticketsLine(tickets)} · attempts inside a run and runs around the loop, counted separately`}
            defaultOpen={tickets.length === 1}
          >

            {tickets.map((ticket) => (
              <div key={ticket.ref} className={css.ticketRow}>
                <div>
                  <span className="mono" style={{ fontSize: 12 }}>
                    {ticket.url ? (
                      <a href={ticket.url} target="_blank" rel="noreferrer">{ticket.ref}</a>
                    ) : (
                      ticket.ref
                    )}
                  </span>
                  <div className={css.ticketAge}>{age(ticket.latestAt)}</div>
                </div>

                <div>
                  <div className={css.ticketTitle}>{ticket.title ?? ticket.ref}</div>
                  <div className={`${css.ticketLanes} mono`}>
                    {ticket.byLane.map((lane) => (
                      <span key={lane.key}>{lane.laneType} ×{lane.runs}</span>
                    ))}
                  </div>
                  {ticket.failureSummary ? (
                    <div className={css.ticketFail}>{ticket.failureSummary}</div>
                  ) : null}
                  {ticket.prUrls.length > 0 ? (
                    <div className={css.ticketLinks}>
                      {/* Every pull request the ticket produced, including one it
                          walked away from: two is the fact worth seeing. */}
                      {ticket.prUrls.map((url, i) => (
                        <a key={url} href={url} target="_blank" rel="noreferrer">
                          {ticket.prUrls.length === 1 ? 'Pull request' : `Pull request ${i + 1}`}
                        </a>
                      ))}
                    </div>
                  ) : null}
                </div>

                <div className={css.ticketState}>
                  <div className={css.ticketStatus} style={{ color: `var(--s-${ticket.stage.token})` }}>
                    {ticket.stage.label}
                  </div>
                  <div className={`${css.ticketMeta} mono`}>
                    {departmentLabel(ticket.department, depts.data ?? [])} · {ticket.laneType}
                  </div>
                  <div className={`${css.ticketMeta} mono`}>
                    attempt {ticket.attempt} of {ticket.attemptCap}
                  </div>
                </div>

                <div className={`${css.num} mono`}>
                  {ticket.runs} {ticket.runs === 1 ? 'run' : 'runs'}
                </div>
                <div className={`${css.num} mono`}>
                  {ticket.hasCost ? usd(ticket.spend, { precise: ticket.spend < 1 }) : '—'}
                </div>
              </div>
            ))}

            <Notice>
              <span style={{ padding: '0 18px', display: 'block' }}>
                Runs grouped by the reference they carry. The attempt is the latest run's own
                retry count and the run count is trips around the loop; they are different
                figures and are never added. This reads the {RUNS_WINDOW} most recent runs
                {dept === 'All' ? '' : ' of this department'}, so it is a window on how work is going now rather than a full history — a ticket whose runs
                have all aged out of it is not here.
              </span>
            </Notice>
          </Disclosure>
        </Card>
      ) : null}
    </>
  )
}

/** One slot on the board: what is in it, where it is on the track, and the links out. */
function LaneCard({ lane, departments }: { lane: Lane; departments: ReadonlyArray<DepartmentRow> }) {
  return (
    <Card>
      <div className={css.lane}>
        <div className={css.laneTop}>
          <div>
            <MicroLabel>Department</MicroLabel>
            <div className={css.laneDept}>{departmentLabel(lane.department, departments)}</div>
            {lane.agent ? (
              <button
                className={css.agentButton}
                title={`${lane.agent} — agent card not built yet`}
                disabled
              >
                <span className={`${css.agentFace} mono`}>{lane.face}</span>
                <span className={`${css.agentId} mono`}>{lane.agent}</span>
              </button>
            ) : null}
            <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 5 }}>
              {lane.laneType}
            </div>
          </div>

          <div>
            <div className={css.laneTask}>{lane.task}</div>
            <div className={`${css.laneMeta} mono`}>
              <span>{lane.externalRef ?? lane.runId.slice(0, 8)}</span>
              <span>{lane.cost}</span>
              <span>{lane.outputTokens} out</span>
              <span>{lane.cacheRead} cache read</span>
              {lane.branch ? <span>{lane.branch}</span> : null}
            </div>
          </div>

          <div className={css.laneRight}>
            <div>
              <div className={`${css.laneElapsed} mono`}>{lane.elapsed}</div>
              <div className={css.laneElapsedNote}>{lane.live ? 'in stage' : 'total'}</div>
            </div>
            <Button
              variant="quiet"
              disabled
              title="Not wired up. Pausing a lane has to stop the runner, which is a change in the runner rather than here."
            >
              Pause
            </Button>
            <div className={css.laneLinks}>
              {lane.externalUrl ? <a href={lane.externalUrl} target="_blank" rel="noreferrer">Issue</a> : null}
              {lane.prUrl ? <a href={lane.prUrl} target="_blank" rel="noreferrer">Pull request</a> : null}
            </div>
          </div>
        </div>

        <StageTrack stage={lane.stage} />
      </div>
    </Card>
  )
}
